import { Platform, Vibration } from 'react-native';
import { Logger } from './logger';

/**
 * The audible/tactile part of an emergency broadcast. Native devices vibrate
 * in an urgent pattern; web plays a siren-style two-tone through WebAudio
 * (no asset file needed, so it works on every deployment). Best-effort by
 * design — an alert that cannot sound must never crash the app.
 */
export function playEmergencyAlert(): void {
  try {
    if (Platform.OS === 'web') {
      playWebSiren();
      return;
    }

    // SOS-ish pattern: three long pulses, repeated once.
    Vibration.vibrate([0, 600, 250, 600, 250, 600], false);
  } catch (error) {
    Logger.warn('Emergency', 'Alert sound/vibration failed', error);
  }
}

/**
 * The everyday arrival sound: a short two-note chime, deliberately nothing like
 * the emergency siren above — a fee reminder must not sound like a lockdown.
 *
 * Synthesised through WebAudio rather than shipped as an asset, for the same
 * reason the siren is: no binary to add to either bundle, and it works on every
 * deployment. Native gets a single short tap instead, since the OS already plays
 * the notification tone for a real push; this only covers the in-app case.
 *
 * Best-effort throughout: a sound that cannot play must never break the feed.
 */
export function playNotificationChime(): void {
  try {
    if (Platform.OS === 'web') {
      playWebChime();
      return;
    }

    // One brief pulse. Anything longer reads as an alarm.
    Vibration.vibrate(120, false);
  } catch (error) {
    Logger.warn('Notification', 'Chime failed', error);
  }
}

function playWebChime(): void {
  const AudioContextCtor =
    (globalThis as any).AudioContext || (globalThis as any).webkitAudioContext;
  if (!AudioContextCtor) return;

  const context: any = new AudioContextCtor();

  // Browsers suspend audio until the page has been interacted with. Resuming is
  // best-effort: if the parent has not touched the page yet the chime is simply
  // skipped rather than throwing.
  context.resume?.().catch(() => undefined);

  const gain = context.createGain();
  gain.connect(context.destination);

  const oscillator = context.createOscillator();
  // Sine, not the siren's square wave: soft rather than harsh.
  oscillator.type = 'sine';
  oscillator.connect(gain);

  const start = context.currentTime;

  // Two rising notes (G5 -> C6), a conventional "something arrived" figure.
  oscillator.frequency.setValueAtTime(784, start);
  oscillator.frequency.setValueAtTime(1047, start + 0.12);

  // Eased in and out so it does not click on either end.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.12, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.42);

  oscillator.start(start);
  oscillator.stop(start + 0.45);
  oscillator.onended = () => context.close().catch(() => undefined);
}

function playWebSiren(): void {
  const AudioContextCtor =
    (globalThis as any).AudioContext || (globalThis as any).webkitAudioContext;
  if (!AudioContextCtor) return;

  // Browser-only API - typed loosely because this file also compiles for native.
  const context: any = new AudioContextCtor();
  const gain = context.createGain();
  gain.gain.value = 0.18;
  gain.connect(context.destination);

  const oscillator = context.createOscillator();
  oscillator.type = 'square';
  oscillator.connect(gain);

  // Two-tone siren: alternate 880/660 Hz for ~2.4s.
  const start = context.currentTime;
  for (let i = 0; i < 6; i++) {
    oscillator.frequency.setValueAtTime(i % 2 === 0 ? 880 : 660, start + i * 0.4);
  }

  oscillator.start(start);
  oscillator.stop(start + 2.4);
  oscillator.onended = () => context.close().catch(() => undefined);
}
